import 'moment/locale/ja';
import { Input, Pagination, Select, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import searchIcon from 'src/assets/images/search-icon.png';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { pageSizeList } from 'src/assets/constants';
import iconSelect from 'src/assets/images/arrow-down-2.png';
import TableNoteNoData from 'src/components/common/tableNoteNoData';
import { useListOrderStatus } from 'src/api/useListOrderStatus';
import { useListOrder } from 'src/api/useListOrder';
import moment from 'moment';
import { numberWithCommas } from 'src/utils/price';
import { getClassStatusOrder, handleGetStatusOrder } from 'src/utils';

interface DataType {
  key: string;
  name: string;
  location: string;
  updated_at: string;
}

const columns: ColumnsType<DataType> = [
  {
    title: '注文番号',
    key: 'order_code',
    dataIndex: 'order_code',
    width: 300,
  },
  {
    title: '氏名',
    dataIndex: 'username',
    key: 'username',
    width: 250,
  },
  {
    title: '合計',
    dataIndex: 'total_price',
    key: 'total_price',
    width: 150,
    align: 'right',
  },
  {
    title: 'ステータス',
    key: 'status',
    dataIndex: 'status',
    width: 250,
    align: 'center',
  },
  {
    title: '注文日時',
    key: 'created_at',
    dataIndex: 'created_at',
    width: 180,
  },
];

const OrderListComponent = () => {
  const history = useNavigate();
  const [keyword, setKeyword] = useState<string>('');
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [status, setStatus] = useState<number | undefined>();
  const [page, setPage] = useState<number>(1);
  const [sizePage, setSizePage] = useState<number>(10);
  const [listStatusData, setListStatusData] = useState([]);
  const { data: listOrderData, isLoading } = useListOrder({ page, size: sizePage, status, keyword: keywordSearch });
  const { data: listStatus } = useListOrderStatus();

  const dataSource = useMemo(() => {
    if (listOrderData) {
      return listOrderData?.records?.data?.map((order: any) => ({
        ...order,
        key: order?.id,
        order_code: (
          <span onClick={() => handleGoOrderDetail(order?.id)} className="cursor-pointer column-redirect">
            {order?.order_code}
          </span>
        ),
        username: <span className="line-1">{order?.username}</span>,
        status: (
          <span className={`company-request-status inline-block ${getClassStatusOrder(order?.status)}`}>
            {handleGetStatusOrder(order?.status)}
          </span>
        ),
        created_at: (
          <span className="site-name line-1 break-all">
            {order?.created_at ? moment(order?.created_at).utc().format('YYYY/MM/DD') : ''}
          </span>
        ),
        total_price: <span className="line-1 break-all">{numberWithCommas(order?.total_price)} 円</span>,
      }));
    }
    return [];
  }, [listOrderData]);

  const iconSearch = () => {
    return (
      <div className="p-[5px] cursor-pointer" onClick={handleSearchKeyword}>
        <img src={searchIcon} alt="icon-search" />
      </div>
    );
  };

  const handleChangeSizePage = (value: number) => {
    setPage(1);
    setSizePage(value);
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleGoOrderDetail = (id: number | undefined) => {
    history(`/order/${id}`);
  };

  const handleSearchKeyword = () => {
    setPage(1);
    setKeywordSearch(keyword?.trim());
    setKeyword(keyword?.trim());
  };

  const handleChangeKeyword = (value: string) => {
    setKeyword(value);
  };

  const handleSearchStatus = (status: number) => {
    setPage(1);
    setStatus(status);
    setKeywordSearch(keyword?.trim());
    setKeyword(keyword?.trim());
  };

  const renderSelectIcon = () => {
    return <img src={iconSelect} alt="icon-select" />;
  };

  useEffect(() => {
    if (!(listStatus && listStatus.length)) return;
    setListStatusData(listStatus);
  }, [listStatus]);

  return (
    <div className="page-content company-page">
      <div className="mb-[16px]">
        <h2 className="page-content-title border-bottom-title">注文一覧</h2>
      </div>
      <div className="page-content-table">
        <div className="flex justify-between items-center p-[16px]">
          <div className="flex items-center">
            <Input
              onPressEnter={handleSearchKeyword}
              className="input-search min-w-[423px] mr-[8px]"
              placeholder="注文番号又は氏名を検索してください"
              suffix={iconSearch()}
              onChange={(e) => handleChangeKeyword(e.target.value)}
              value={keyword}
            />
            <Select
              options={listStatusData}
              placeholder="ステータス"
              className="company-select h-[40px] w-[200px]"
              allowClear={true}
              onChange={(value: number) => handleSearchStatus(value)}
              suffixIcon={renderSelectIcon()}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={isLoading}
          locale={{
            emptyText: <TableNoteNoData />,
          }}
        />

        <div className="py-[20px] px-[12px] flex justify-between items-center">
          <div className="pagination-info">
            {listOrderData?.limitFrom} - {listOrderData?.limitTo} 件表示/全 {listOrderData?.totalRow} 件中
          </div>
          <div className="flex">
            <Pagination
              current={page}
              total={listOrderData?.totalRow}
              className="pagination-main mr-[20px]"
              showSizeChanger={false}
              pageSize={sizePage}
              onChange={(page) => handleChangePage(page)}
            />
            <Select
              defaultValue={10}
              style={{ width: 153 }}
              className="pagination-options"
              onChange={(e) => handleChangeSizePage(e)}
              options={pageSizeList}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderListComponent;
